import React from "react";
import { Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import TranslateText from "../../../resuableComponents/TranslateText";
import { selectIsMobileLayout } from "../../../../slices/MobileLayoutSlice";
import { selectDarkColor, selectLightColor } from "../../../../slices/PaymentSessionDetailsSlice";
import { generateDynamicPaymentMethodLogo, getPaymentMethodIconColors } from "../../../../Utils/utils";

// Same types the API reports attempts for (fieldErrorItems), in the order of the payment tabs.
const SELECTABLE_METHODS = ["Upi", "UpiOneTimeMandate", "Card", "Wallet", "NetBanking", "BuyNowPayLater"];

// The payment methods of this session that still have attempts left. The API reports
// attempts for every method, so a method counts only when the session offers it
// (paymentMethodDictionary) and the response says it has attempts left. Card is always
// available, its retry limit is ignored.
export const getAvailablePaymentMethods = (paymentMethodDictionary, methodAttemptsLeft, isMobileLayout) => {
  // UPI is hidden in the tabs when it only has intent apps (desktop) or only QR (mobile).
  const isMethodShown = (type) => {
    if (type !== "Upi") return true;
    const brands = paymentMethodDictionary.Upi.map((method) => method?.brand);
    return isMobileLayout ? !brands.every((brand) => brand === "UpiQr") : brands.some((brand) => brand !== "UpiIntent");
  };

  return SELECTABLE_METHODS
    .filter((type) => paymentMethodDictionary?.[type]?.length && (type === "Card" || methodAttemptsLeft[type] > 0) && isMethodShown(type));
};

// The available methods inside the retry limit modal, with the same icon and title as the payment tabs.
const AvailablePaymentMethods = ({ onSelect }) => {
  const paymentMethodDictionary = useSelector((state) => state.paymentSessionDetails.paymentMethodDictionary);
  const methodAttemptsLeft = useSelector((state) => state.paymentInitiation.methodAttemptsLeft);
  const isMobileLayout = useSelector(selectIsMobileLayout);
  const darkColor = useSelector(selectDarkColor);
  const lightColor = useSelector(selectLightColor);
  const { iconStroke, iconFill } = getPaymentMethodIconColors(darkColor, lightColor);

  const availableMethods = getAvailablePaymentMethods(paymentMethodDictionary, methodAttemptsLeft, isMobileLayout);

  if (!availableMethods.length) return null;

  // Same title as the payment tab.
  const getTitle = (type) => {
    const methods = paymentMethodDictionary[type];
    if (methods.length === 1 && ["Wallet", "BuyNowPayLater"].includes(methods[0]?.type)) return methods[0]?.title;
    if (type === "BuyNowPayLater") return "Buy Now Pay Later";
    return <TranslateText label={type.toUpperCase()}>{methods[0]?.typeTitle}</TranslateText>;
  };

  return (
    <div className="domestic-error-methods dynamic-font-styling">
      <Typography className="domestic-error-methods-title">
        <TranslateText label={"TRY_ANOTHER_PAYMENT_METHOD"}>Try another payment method</TranslateText>
      </Typography>
      <div className="domestic-error-methods-list">
        {availableMethods.map((type) => (
          <button key={type} className="domestic-error-method-row" onClick={() => onSelect(type)}>
            {generateDynamicPaymentMethodLogo(type === "UpiOneTimeMandate" ? "Upi" : type, iconStroke, iconFill, "24px", "24px")}
            <Typography className="domestic-error-method-title dynamic-font-family">{getTitle(type)}</Typography>
            <RightOutlined className="domestic-error-method-arrow" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default AvailablePaymentMethods;
